// Shared pipeline-stage derivation for Home's "Recent projects" list and the
// Projects table's stage pill + filter tabs. This wraps the SAME single
// source of truth already used by ProjectView/WorkflowProgressBar
// (components/orchestrator/workflowStages.ts) so a project's stage can never
// tell a different story on the list screens than it does on its own page.
//
// Deliberately cheap: only fields already returned by getProjects() (list
// endpoint) plus the disclosure agreements list (already fetched once for the
// Home disclosure panels) are used — no per-row detail calls, so this is safe
// to run for every row in a table.
//
// Two real backend signals the list endpoint does not expose are honestly
// approximated rather than invented:
//   hasScores   — the list has no independent "assessment scores exist"
//                 field, so it is approximated as "analysis completed"
//                 (ai_analysis_status === 'completed'). This means the pill
//                 will not show a distinct "Assessment in progress" state
//                 between Analysis and Review — it favours staying accurate
//                 about the LATER stages (Review/Matching/Disclosure), which
//                 ARE backed by real fields (review_status, discoverable,
//                 disclosure agreements), over inventing a middle state we
//                 cannot verify.
//   matchCount  — rather than inferring "matched" from `discoverable` alone
//                 (a project can be discoverable with zero real matches), a
//                 project only counts as matched once a real disclosure
//                 agreement referencing it exists.
import type { Project, DisclosureAgreement } from '../types'
import { computeWorkflowStages, type ProgressStage } from '../components/orchestrator/workflowStages'
import type { StatusTone } from '../components/ui/StatusChip'

/** Disclosure agreements that reference a given project. */
export function agreementsForProject(
  agreements: DisclosureAgreement[] | undefined,
  projectId: string,
): DisclosureAgreement[] {
  return (agreements ?? []).filter((a) => a.project_id === projectId)
}

/** True once any agreement for this project has reached full disclosure (L4+). */
export function isProjectDisclosed(project: Project, agreements: DisclosureAgreement[] | undefined): boolean {
  return agreementsForProject(agreements, project.project_id).some((a) => (a.current_level ?? 0) >= 4)
}

export function deriveProjectWorkflowStages(
  project: Project,
  agreements: DisclosureAgreement[] | undefined,
): ProgressStage[] {
  const projectAgreements = agreementsForProject(agreements, project.project_id)
  const hasAnalysis = project.ai_analysis_status === 'completed'
  return computeWorkflowStages({
    hasDocuments: project.document_count > 0,
    hasDeclaration: !!project.invention_declaration,
    hasAnalysis,
    hasScores: hasAnalysis,
    hasReviewApproved: project.review_status === 'approved',
    hasMatching: !!project.discoverable,
    matchCount: projectAgreements.length,
    runStatus: '',
    disclosureComplete: isProjectDisclosed(project, agreements),
  })
}

export interface StagePill {
  label: string
  tone: StatusTone
}

const STAGE_TONE: Record<string, StatusTone> = {
  Upload: 'neutral',
  Intake: 'neutral',
  Analysis: 'info',
  Assessment: 'info',
  Review: 'warning',
  Matching: 'info',
  Disclosure: 'info',
}

/** The single pipeline-stage pill shown for a project on Home and the Projects table. */
export function getProjectStagePill(project: Project, agreements: DisclosureAgreement[] | undefined): StagePill {
  if (isProjectDisclosed(project, agreements)) {
    return { label: 'Disclosed', tone: 'success' }
  }
  const stages = deriveProjectWorkflowStages(project, agreements)
  const active = stages.find((s) => s.status === 'active')
  // Every stage completed but not yet flagged disclosed above — edge case,
  // shows the terminal stage name rather than nothing.
  if (!active) return { label: 'Disclosure', tone: 'info' }
  const label = active.name === 'Upload' && project.status === 'draft' ? 'Upload · Draft' : active.name
  return { label, tone: STAGE_TONE[active.name] ?? 'neutral' }
}

export type ProjectTabKey = 'all' | 'active' | 'disclosed' | 'draft'

export const PROJECT_TABS: { key: ProjectTabKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'disclosed', label: 'Disclosed' },
  { key: 'draft', label: 'Draft' },
]

/** Whether a project belongs in the given filter tab. */
export function projectMatchesTab(
  project: Project,
  tab: ProjectTabKey,
  agreements: DisclosureAgreement[] | undefined,
): boolean {
  if (tab === 'all') return true
  if (tab === 'disclosed') return isProjectDisclosed(project, agreements)
  if (tab === 'draft') return project.status === 'draft'
  // 'active' — live projects that haven't already reached full disclosure.
  return project.status === 'active' && !isProjectDisclosed(project, agreements)
}
